import { uuidv4 } from '@deepcloud/deep-ui-utils'
import { useFileUploader } from '@/components/upload/composables/use-file-uploader.ts'
import { useFolderTreeUploader } from '@/components/upload/composables/use-folder-tree-uploader.ts'
import { TaskUpload, TaskUploadStatus, UploadNodes } from '../types'
import { useTaskUploadQueue } from './use-task-upload-queue.ts'
import { useTaskUploads } from './use-task-uploads.ts'
import { useUploadQueue } from './use-upload-queue'

const MAX_CONCURRENT_UPLOADS = 3
const useGlobalUploadQueue = createSharedComposable(useUploadQueue)

const { enqueueUpload } = useGlobalUploadQueue(MAX_CONCURRENT_UPLOADS)

const useGlobalTaskUploadQueue = createSharedComposable(useTaskUploadQueue)
const { taskUploadQueue, enqueueTaskUploadId, dequeueTaskUploadId } =
  useGlobalTaskUploadQueue()

export function useTaskUploadUploader(
  taskUpload: MaybeRefOrGetter<TaskUpload[]>,
) {
  const { getTaskUploadById, updateTaskUploadItemStatus } =
    useTaskUploads(taskUpload)

  const taskUploads = useTaskUploads(taskUpload)
  const { onUploadFolderTree } = useFolderTreeUploader(taskUploads)
  const { onUploadFileItem } = useFileUploader(taskUploads)

  const blockingTaskId = ref<number | null>(null)
  function getCountActiveUploads(): number {
    let total = 0
    const activeStatuses = new Set<TaskUploadStatus>(['processing']) // only processing counts as active

    toValue(taskUpload).forEach((task) => {
      if (!task) return

      total +=
        task.files?.filter((f) => activeStatuses.has(f.status)).length ?? 0
      total += Object.values(task.folders || {}).filter((f) =>
        activeStatuses.has(f.status),
      ).length
    })

    return total
  }

  function hasFolders(task: TaskUpload | undefined): boolean {
    return !!task && Object.keys(task.folders || {}).length > 0
  }

  const runningTaskUploads = new Set<number>()

  async function startUploadsFromTask(task: TaskUpload) {
    if (runningTaskUploads.has(task.id)) {
      console.log(
        `Task ${task.id} upload already running, skipping duplicate start.`,
      )
      return
    }
    runningTaskUploads.add(task.id)
    console.log(`Starting uploads for task ${task.id}`)

    try {
      // Guard: if the task was canceled before we even started, bail out
      if (task.status === 'canceled') return

      // Upload the entire folder tree in one API call (enqueued so it respects
      // the global concurrency limit while it runs).
      if (hasFolders(task)) {
        await new Promise<void>((resolve, reject) => {
          enqueueUpload(async () => {
            // Re-check after dequeue - task may have been canceled while queued
            if (task.status === 'canceled') {
              resolve()
              return
            }
            try {
              await onUploadFolderTree(task.id)
              resolve()
            } catch (error) {
              reject(error)
            }
          })
        })
      }
      // Re-check after folders — task may have been canceled during folder upload
      if ((task.status as TaskUploadStatus) === 'canceled') return

      await Promise.all(
        (task.files || []).map((fileNode, idx) => {
          if (fileNode.status === 'completed') return Promise.resolve()
          return new Promise<void>((resolve) => {
            enqueueUpload(async () => {
              if (fileNode.status === 'canceled') {
                fileNode.abortController?.abort()
                resolve()
                return
              }
              await onUploadFileItem(task.id, idx)
              resolve()
            })
          })
        }),
      )

      if (blockingTaskId.value === task.id) {
        blockingTaskId.value = null
      }
      updateTaskUploadItemStatus(task.id)
      tryStartNextTaskUpload()
    } finally {
      runningTaskUploads.delete(task.id)
    }
  }

  let isStartingUploads = false

  function tryStartNextTaskUpload() {
    if (isStartingUploads) return
    isStartingUploads = true

    try {
      const countActiveUploads = getCountActiveUploads()
      if (countActiveUploads >= MAX_CONCURRENT_UPLOADS) {
        console.log('Max concurrency reached. Waiting.')
        return
      }

      if (blockingTaskId.value !== null) {
        const blockingTask = getTaskUploadById(blockingTaskId.value)
        if (!blockingTask) {
          blockingTaskId.value = null
          tryStartNextTaskUpload() // try again for next task
          return
        }
        void startUploadsFromTask(blockingTask)
        return
      }

      while (
        taskUploadQueue.value.length > 0 &&
        getCountActiveUploads() < MAX_CONCURRENT_UPLOADS
      ) {
        const nextTaskId = taskUploadQueue.value[0]
        const nextTask = getTaskUploadById(nextTaskId)

        if (!nextTask) {
          dequeueTaskUploadId()
          continue
        }

        if (hasFolders(nextTask)) {
          blockingTaskId.value = nextTaskId
          dequeueTaskUploadId()
          void startUploadsFromTask(nextTask)
          break // stop after blocking task
        } else {
          dequeueTaskUploadId()
          void startUploadsFromTask(nextTask)
        }
      }
    } finally {
      isStartingUploads = false
    }
  }

  function startTaskUploadUploaderQueued(taskUploadId: number) {
    enqueueTaskUploadId(taskUploadId)

    tryStartNextTaskUpload()
  }

  function setNodesToUpload<M, T extends UploadNodes<M>>(payload: T) {
    const { folders, files, metadata, target, onSuccessFolder, onSuccessFile } =
      payload

    const hasFolders = folders && Object.keys(folders).length > 0
    const hasFiles = files && files.length > 0

    // Case 1: folders exist (possibly with files)
    if (hasFolders) {
      const taskUploadId = toValue(taskUpload).length
      toValue(taskUpload)[taskUploadId] = {
        id: taskUploadId,
        status: 'added',
        progress: 0,
        folders: {},
        files: [],
        metadata,
        target,
      } as TaskUpload

      // Add folders
      Object.keys(folders!).forEach((folderId) => {
        toValue(taskUpload)[taskUploadId].folders![folderId] = {
          id: folderId,
          status: 'added',
          progress: 0,
          data: { ...folders![folderId] },
          onSuccess: onSuccessFolder ?? undefined,
        }
      })

      // Add files (if any)
      if (hasFiles) {
        files!.forEach((file) => {
          toValue(taskUpload)[taskUploadId].files!.push({
            id: uuidv4(),
            status: 'added',
            progress: 0,
            data: { ...file },
            onSuccess: onSuccessFile ?? undefined,
          })
        })
      }

      startTaskUploadUploaderQueued(taskUploadId)

      // Case 2: no folders, only files
    } else if (hasFiles) {
      files!.forEach((file) => {
        const taskUploadId = toValue(taskUpload).length
        toValue(taskUpload)[taskUploadId] = {
          id: taskUploadId,
          status: 'added',
          progress: 0,
          folders: {},
          files: [
            {
              id: uuidv4(),
              status: 'added',
              progress: 0,
              data: { ...file },
              onSuccess: onSuccessFile ?? undefined,
            },
          ],
          metadata,
          target,
        } as TaskUpload

        startTaskUploadUploaderQueued(taskUploadId)
      })
    }
  }

  // ---- Reactive watcher ----
  // React to TaskUpload status transitions (cancel/error → added, or cancel → abort)
  watch(
    () => toValue(taskUpload).map((tu) => tu.status),
    (newStatuses, oldStatuses) => {
      newStatuses.forEach((status, idx) => {
        const prev = oldStatuses?.[idx]
        const tu = toValue(taskUpload)[idx]

        // Handle retry transitions: (error/canceled → added)
        if ((prev === 'canceled' || prev === 'error') && status === 'added') {
          let didResetSome = false

          // Reset folders: only those with status 'error' or 'canceled'
          Object.values(tu.folders || {}).forEach((f) => {
            if (f.status === 'error' || f.status === 'canceled') {
              f.status = 'added'
              didResetSome = true
            }
          })

          // Reset files: only those with status 'error' or 'canceled'
          tu.files?.forEach((f) => {
            if (f.status === 'error' || f.status === 'canceled') {
              f.status = 'added'
              didResetSome = true
            }
          })

          if (didResetSome) {
            tu.progress = 0
            tu.status = 'added'

            const enqueue = () => {
              if (runningTaskUploads.has(tu.id)) {
                // Previous run still winding down, keep polling
                void nextTick(enqueue)
              } else {
                startTaskUploadUploaderQueued(tu.id)
              }
            }
            enqueue()
          }
        }

        // Handle cancel transitions: (any → canceled)
        if (prev !== 'canceled' && status === 'canceled') {
          // Abort any files that are still uploading/processing
          tu.files?.forEach((f) => {
            if (f.status === 'processing') {
              // cancel the request
              f.abortController?.abort()
              // reset file state
              f.status = 'canceled'
              f.progress = 0
              if (f.data?.uploadSize) {
                f.data = {
                  ...f.data,
                  uploadSize: { ...f.data.uploadSize, uploaded: 0 },
                }
              }
            } else if (f.status === 'added') {
              f.status = 'canceled'
            }
          })

          // Optional: also abort folder-level uploads if any
          Object.values(tu.folders || {}).forEach((folder) => {
            if (folder.status === 'processing') {
              // cancel the request
              folder.abortController?.abort?.()
              // reset file state
              folder.status = 'canceled'
              folder.progress = 0
            } else if (folder.status === 'added') {
              folder.status = 'canceled'
              folder.progress = 0
            }
          })

          tu.status = 'canceled'

          // Remove from upload queue if still queued
          const idxInQueue = taskUploadQueue.value.indexOf(tu.id)
          if (idxInQueue !== -1) taskUploadQueue.value.splice(idxInQueue, 1)
        }
      })
    },
    { deep: true },
  )

  return {
    setNodesToUpload,
  }
}
